import React, {
	ChangeEvent,
	Dispatch,
	SetStateAction,
	useEffect,
	useRef,
	useState,
} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import { useAuthStore } from "@onair/store/auth";
import { toast } from "@onair/minimal-ui/snackbar";
import { GetResourceResponseDto, ResourceType } from "@onair/repositories";
import {
	getVideoDuration,
	isVideoFile,
	uploadToS3,
	uploadToS3V2,
} from "@onair/utils/upload-to-s3";

// import { CONFIG } from "#src/configs/config-global";
import { CONFIG } from "../../../../configs/config-global";
// import {
// 	useCreateResourceMutation,
// 	useDeleteResourceMutation,
// 	useUpdateResourceMutation,
// } from "#src/operations";

import {
	useCreateResourceMutation,
	useDeleteResourceMutation,
	useUpdateResourceMutation,
} from "@onair/operations";

const MAX_FILE_SIZE_BYTES = CONFIG.site.maxUploadFileSizeBytes;
const FILE_BUCKET_MAP = {
	"video/quicktime": CONFIG.site.uploadVideoBucket,
	"video/mp4": CONFIG.site.uploadVideoBucket,
	"video/mov": CONFIG.site.uploadVideoBucket,
	"video/mpg": CONFIG.site.uploadVideoBucket,
	"video/flv": CONFIG.site.uploadVideoBucket,
	"video/f4v": CONFIG.site.uploadVideoBucket,
	"video/m4v": CONFIG.site.uploadVideoBucket,
	"video/asf": CONFIG.site.uploadVideoBucket,
	"video/wmv": CONFIG.site.uploadVideoBucket,
	"video/vob": CONFIG.site.uploadVideoBucket,
	"video/3gp": CONFIG.site.uploadVideoBucket,
	"video/mkv": CONFIG.site.uploadVideoBucket,
	"video/divx": CONFIG.site.uploadVideoBucket,
	"video/xvid": CONFIG.site.uploadVideoBucket,
	"image/png": CONFIG.site.uploadPDFBucket,
	"image/jpeg": CONFIG.site.uploadPDFBucket,
	"image/jpg": CONFIG.site.uploadPDFBucket,
	"application/pdf": CONFIG.site.uploadPDFBucket,
	"application/vnd.ms-excel": CONFIG.site.uploadPDFBucket,
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
		CONFIG.site.uploadPDFBucket,
};

const ACCEPTED_FILE_TYPES = Object.keys(FILE_BUCKET_MAP).join(",");

interface IUploadFileProps {
	libraryId: string;
	refetch: () => void;
	setProgress: Dispatch<SetStateAction<number>>;
	setTotalUploadFile: Dispatch<SetStateAction<number>>;
	setTotalCompleteUploadFile: Dispatch<SetStateAction<number>>;
	setCurrentFileUpload: Dispatch<SetStateAction<string | null>>;
}

const UploadFile = (props: IUploadFileProps) => {
	const {
		libraryId,
		refetch,
		setProgress,
		setTotalUploadFile,
		setTotalCompleteUploadFile,
		setCurrentFileUpload,
	} = props;

	const currentUser = useAuthStore();
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [fileProgress, setFileProgress] = useState<Record<string, number>>({});

	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const abortControllers = useRef<Map<string, AbortController>>(new Map());

	const { mutateAsync: createResource } = useCreateResourceMutation({
		onError: () => toast.error("Có lỗi xảy ra. Vui lòng thử lại sau"),
	});

	const { mutateAsync: updateResourceMutationAsync } =
		useUpdateResourceMutation({
			onError: () => toast.error("Có lỗi xảy ra. Vui lòng thử lại sau"),
		});

	const { mutateAsync: deleteResourceMutationAsync } =
		useDeleteResourceMutation({
			onError: () => toast.error("Có lỗi xảy ra. Vui lòng thử lại sau"),
		});

	const handleUploadFile = async () => {
		fileInputRef?.current?.click();
	};

	const validateFile = (file: File): string | null => {
		console.log(file.type, "file.type");
		if (!FILE_BUCKET_MAP[file.type]) {
			return "Tệp không hợp lệ. Chỉ hỗ trợ định dạng video, hình ảnh, hoặc PDF.";
		}
		if (file.size > MAX_FILE_SIZE_BYTES) {
			return "Tệp quá lớn. Giới hạn là 4GB.";
		}
		return null;
	};
	useEffect(() => {
		console.log("fileProgress", fileProgress);
	}, [fileProgress]);
	const handleUpload = async (file: File, bucket: string) => {
		const controller = new AbortController();
		abortControllers.current.set(file.name, controller);

		const duration = isVideoFile(file)
			? await getVideoDuration(file)
			: undefined;

		const fileNameFormat = slugify(file.name);
		const resource = await createResource({
			name: file.name,
			fileName: file.name,
			libraryId: libraryId,
			capacity: file.size,
			fileType: file.type,
			resourceType: ResourceType.FILE,
			duration,
		});

		const resourceData = resource?.data as GetResourceResponseDto;
		if (!resourceData || !resourceData?.id) {
			toast.error("Tạo tài nguyên thất bại.");
			return;
		}

		try {
			const fileName = `${currentUser.id}/${libraryId}/${resourceData.id}_${fileNameFormat}`;

			const [uploadedFile] = await uploadToS3V2(
				[{ file, fileName }],
				bucket,
				(progress) => {
					setFileProgress((prev) => {
						const updatedProgress = { ...prev, [file.name]: progress };
						// setProgress(calculateTotalProgress(updatedProgress)); // Update total progress
						setProgress(progress);
						return updatedProgress;
					});
				},
				controller,
			);

			if (uploadedFile && uploadedFile.fileUrl) {
				await updateResourceMutationAsync({
					id: resourceData.id,
					data: {
						name: fileName,
						fileName: file.name,
						linkUrl: uploadedFile.fileUrl,
						...(!isVideoFile(file) && {
							publicUrl: uploadedFile.fileUrl,
							linkUrl: uploadedFile.fileUrl,
							thumbnailUrl: uploadedFile.fileUrl,
						}),
					},
				});
			}
		} catch (error: any) {
			if (error.message === "Upload cancelled") {
				toast.info("Tải lên đã bị huỷ.");
				await deleteResourceMutationAsync(resourceData.id);
			} else {
				toast.error("Tải file thất bại. Vui lòng thử lại.");
				console.error("Upload Error:", error.message);
			}
		} finally {
			abortControllers.current.delete(file.name);
		}
	};

	const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);
		if (files.length === 0) return;

		const invalidFiles = files.filter((file) => validateFile(file));
		invalidFiles.forEach((file) => toast.error(validateFile(file)!));
		const validFiles = files.filter((file) => !validateFile(file));

		setIsUploading(true);
		setFileProgress(
			Object.fromEntries(validFiles.map((file) => [file.name, 0])),
		);
		setTotalUploadFile(validFiles.length);
		setTotalCompleteUploadFile(0);

		// const uploads = validFiles.map((file) => {
		// 	const bucket = FILE_BUCKET_MAP[file.type];
		// 	return bucket
		// 		? handleUpload(file, bucket)
		// 		: toast.error(`${file.name}: Bucket không hợp lệ.`);
		// });

		// await Promise.allSettled(uploads).then(() => {
		// 	setProgress(calculateTotalProgress(fileProgress));
		// 	setIsUploading(false);
		// 	setFileProgress({});
		// 	fileInputRef.current && (fileInputRef.current.value = "");
		// });

		for (const file of validFiles) {
			const bucket = FILE_BUCKET_MAP[file.type];
			if (bucket) {
				setCurrentFileUpload(file.name);
				await handleUpload(file, bucket);
			} else {
				toast.error(`${file.name}: Bucket không hợp lệ.`);
			}
			refetch();

			setTotalCompleteUploadFile((prevCount) => prevCount + 1);
			toast.success(`${file.name} tải lên thành công.`);
			setProgress(0);
		}
		setCurrentFileUpload(null);
		setIsUploading(false);
		setFileProgress({});
	};

	const handleCancelUpload = () => {
		abortControllers.current.forEach((controller) => controller.abort());
		abortControllers.current.clear();
		setIsUploading(false);
		toast.info("Tất cả tải lên đã bị huỷ.");
	};

	return (
		<Box>
			{isUploading ? (
				<Button
					onClick={handleCancelUpload}
					variant="outlined"
					sx={{
						whiteSpace: "nowrap",
					}}
				>
					Huỷ tải lên
				</Button>
			) : (
				<Button
					className="w-full"
					variant="contained"
					size="medium"
					endIcon={<CloudUploadIcon />}
					onClick={handleUploadFile}
					disabled={isUploading}
					sx={{
						whiteSpace: "nowrap",
					}}
				>
					Tải lên
				</Button>
			)}

			{/* Hidden file input */}
			<input
				type="file"
				multiple
				accept={ACCEPTED_FILE_TYPES}
				ref={fileInputRef}
				className="hidden"
				onChange={handleFileChange}
			/>
		</Box>
	);
};

export default UploadFile;

function slugify(text) {
	return text
		.toString() // Ensure the text is a string
		.toLowerCase() // Convert to lowercase
		.trim() // Remove whitespace from both ends
		.replace(/[^a-z0-9 .-]/g, "") // Allow dots (.) and dashes (-), remove other special characters
		.replace(/\s+/g, "-") // Replace spaces with dashes
		.replace(/-+/g, "-"); // Replace multiple dashes with a single dash
}

const calculateTotalProgress = (
	progressMap: Record<string, number>,
): number => {
	const progressValues = Object.values(progressMap);
	return progressValues.length === 0
		? 0
		: Math.round(
				progressValues.reduce((sum, value) => sum + value, 0) /
					progressValues.length,
			);
};
